import { Button } from '@/components/common/Button';
import { DialogWrapper } from '@/components/common/DialogWrapper';
import { Input } from '@/components/common/Input';
import type { StockBatchRow } from '@/domain/Stock';
import { useEffect, useState } from 'react';

export interface EditStockFormData {
  expire_date: string;
  lot_number: string;
}

interface StockEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batchRow: StockBatchRow | null;
  onSubmit: (id: number, data: EditStockFormData) => void;
  isPending?: boolean;
}

const getTomorrowDate = (): string => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split('T')[0];
};

export function StockEditDialog({
  open,
  onOpenChange,
  batchRow,
  onSubmit,
  isPending,
}: StockEditDialogProps) {
  const [expireDate, setExpireDate] = useState('');
  const [lotNumber, setLotNumber] = useState('');

  useEffect(() => {
    if (open && batchRow) {
      setExpireDate(batchRow.expireDate?.split('T')[0] || '');
      setLotNumber(batchRow.lotNumber || '');
    }
  }, [open, batchRow]);

  const isValid =
    batchRow?.stockId &&
    expireDate &&
    lotNumber.trim().length > 0 &&
    lotNumber.trim().length <= 20;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (batchRow && isValid) {
      onSubmit(batchRow.stockId, {
        expire_date: expireDate,
        lot_number: lotNumber.trim(),
      });
    }
  };

  return (
    <DialogWrapper
      open={open}
      onOpenChange={onOpenChange}
      title="Edit stock batch"
      maxWidth="md"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="text-table-foreground mb-1 block text-sm font-medium">
            Name
          </label>
          <Input
            id="edit-name"
            value={batchRow?.supplementName || ''}
            disabled
            className="bg-white"
          />
        </div>

        <div>
          <label className="text-table-foreground mb-1 block text-sm font-medium">
            Item Strength
          </label>
          <Input
            id="edit-strength"
            value={batchRow?.strength || ''}
            disabled
            className="bg-white"
          />
        </div>

        <div>
          <label className="text-table-foreground mb-1 block text-sm font-medium">
            Expiry date
          </label>
          <Input
            id="edit_expire_date"
            type="date"
            value={expireDate}
            onChange={(e) => setExpireDate(e.target.value)}
            className="bg-white"
            min={getTomorrowDate()}
          />
        </div>

        <div>
          <label className="text-table-foreground mb-1 block text-sm font-medium">
            LOT number
          </label>
          <Input
            id="edit_lot_number"
            value={lotNumber}
            onChange={(e) => setLotNumber(e.target.value)}
            className="bg-white"
            maxLength={20}
          />
        </div>

        <Button
          type="submit"
          variant="primary"
          size="md"
          disabled={isPending || !isValid}
          className="mt-2 w-48"
        >
          {isPending ? 'Saving...' : 'Save changes'}
        </Button>
      </form>
    </DialogWrapper>
  );
}
